import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  H1,
  H2,
  TextMain,
  TextXSmall,
} from '@components/Typography/Typography';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { motion } from 'framer-motion';

import { useActivateVipStatusMutation } from '@/api/queries/user';
import { crownImages } from '@/assets/img/common';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type ActiveteVipStatusRequestType } from '@/common/types/query/user';
import { PageBackground } from '@/components/ui/Backgrounds/PageBackground/PageBackground';
import { BorderedWrapper } from '@/components/ui/BorderedWrapper/BorderedWrapper';
import { YellowButton } from '@/components/ui/Button/Button';
import { FortuneWheel } from '@/components/ui/FortuneWheel/FortuneWheel';
import { Logo } from '@/components/ui/Icons';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import {
  changeVipStatus,
  endFirstVipRegistration,
} from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { type OboardStatusChangeFunction } from '../../model/Config';

import css from './styles.module.scss';

import backgroundImagePng from '/img/firstVipRegistrationPage/background.png';
import backgroundImageWbp from '/img/firstVipRegistrationPage/background.webp';
import TonyVip from '/img/VIP/tony_vip.png';
import TonyVipWebp from '/img/VIP/tony_vip.webp';

interface Props {
  handleStatusChange: OboardStatusChangeFunction;
}

const FirstVipRegistrationPage: FC<Props> = ({ handleStatusChange }) => {
  const userLevel = useAppSelector((state) => state.user.user?.level);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [activateVip, { isLoading }] = useActivateVipStatusMutation();
  const availableRegistrationOffer = useAppSelector(
    (state) => state.user.vip?.paidVIPCost.availableRegistrationOffer,
  );

  const activateFreeVip = (): void => {
    if (!availableRegistrationOffer) {
      handleStatusChange('completed');

      return;
    }
    const body: ActiveteVipStatusRequestType = {
      type: 'Registration',
    };
    activateVip(body)
      .unwrap()
      .then(() => {
        dispatch(changeVipStatus(true));
        SendAnalitics.getVipStatus({ level: userLevel || 0, type: 'free' });
        dispatch(endFirstVipRegistration());
        handleStatusChange('completed');
      })
      .catch((err: FetchBaseQueryError) => {
        if (
          err.data &&
          (err.data as { message: string }).message ===
            "You can't activate registration VIP twice"
        ) {
          showToast(t("You can't activate registration VIP twice"));
          dispatch(endFirstVipRegistration());
          handleStatusChange('completed');

          return;
        }
        showToast('Something went wrong, please try again');
      });
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={css.layout}
    >
      <div className={'top-2 absolute left-1/2 -translate-x-1/2 z-50'}>
        <Logo
          style={{
            marginTop: 'calc(var(--tg-safe-area-inset-top, 0px) + 5px)',
          }}
          className="h-auto w-[61.51px]"
        />
      </div>
      <motion.div
        initial={{
          y: 24,
          opacity: 0,
        }}
        animate={{
          y: 0,
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        transition={{
          ease: 'easeOut',
          duration: 0.5,
        }}
        className="w-full h-full bg-main-bg flex flex-col pb-16 md-h:pb-5"
      >
        <PageBackground
          backgroundImage={{
            png: backgroundImagePng,
            webp: backgroundImageWbp,
          }}
          variant="onboarding"
          className="shrink flex-1 !h-auto relative"
          pictureClassName="absolute left-1/2 -translate-x-1/2 w-[115%] top-0 aspect-square"
        >
          <Picture
            img={TonyVip}
            webp={TonyVipWebp}
            alt="Tony VIP"
            className="absolute w-full bottom-0 left-0"
          />
        </PageBackground>
        <div className="flex items-center flex-col px-10 relative z-[2] -mt-6">
          <Picture
            className="size-12 mb-2"
            img={crownImages.default.png}
            webp={crownImages.default.webp}
            alt="crown"
          />
          <H1 weight="bold" className="leading-none mb-5">
            {t('Join the VIP CLUB')}
          </H1>
          <TextMain className="!leading-1.3 max-w-52 text-center mb-5">
            {t('Win USD by completing “Daily Goods” contracts')}
          </TextMain>
          <BorderedWrapper className="px-6 py-3 mb-8">
            <TextXSmall weight="extrabold" className="uppercase !text-gold">
              {t('available from level 2')}
            </TextXSmall>
          </BorderedWrapper>

          <FortuneWheel />
          <YellowButton
            className="mt-10"
            isLoading={isLoading}
            onClick={activateFreeVip}
            text=""
          >
            <motion.div
              initial={{ x: 40, opacity: 0 }} // ← прилетает справа
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <H2 className="!text-lg">{t('GET VIP 3 DAYS FREE')}</H2>
            </motion.div>
          </YellowButton>
        </div>
      </motion.div>
      <div className={css.effect}></div>
    </motion.main>
  );
};

export { FirstVipRegistrationPage };
